import React from "react";
import { Text, useComputedColorScheme, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import TravelAnimationSection from "./TravelAnimationSection";
import GallerySection from "./GallerySection";
import { SectionConfig } from "../slideshow/AlbumSlideshow";

export interface SplitSectionProps {
  title?: string;
  leftSection: SectionConfig;
  rightSection: SectionConfig;
  leftWidth?: number; // Percentage (0-100)
  rightWidth?: number; // Percentage (0-100)
  gap?: number; // Gap between sections in pixels
  autoPlay?: boolean;
  autoPlayDelayMs?: number;
}

export default function SplitSection({
  title = "Split View",
  leftSection,
  rightSection,
  leftWidth = 50,
  rightWidth = 50,
  gap = 16,
  autoPlay = false,
  autoPlayDelayMs = 3000,
}: SplitSectionProps) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const colorScheme = useComputedColorScheme();

  // Normalize widths to ensure they add up to 100%
  const totalWidth = leftWidth + rightWidth;
  const normalizedLeftWidth = (leftWidth / totalWidth) * 100;
  const normalizedRightWidth = (rightWidth / totalWidth) * 100;

  // On mobile, stack sections vertically
  if (isMobile) {
    return (
      <div className="flex flex-col w-full h-full gap-4">
        <div className="w-full h-full flex flex-col gap-4">
          {/* Left section on top */}
          <div className="h-[30rem] min-h-[25rem]">
            {renderSection(leftSection)}
          </div>
          {/* Right section on bottom */}
          <div className="">{renderSection(rightSection)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full gap-4">
      <div className="w-full h-full flex" style={{ gap: `${gap}px` }}>
        {/* Left section */}
        <div
          className="flex-1"
          style={{
            width: `${normalizedLeftWidth}%`,
            minWidth: 0, // Prevents flex item from overflowing
          }}
        >
          {renderSection(leftSection)}
        </div>

        {/* Right section */}
        <div
          className="flex-1"
          style={{
            width: `${normalizedRightWidth}%`,
            minWidth: 0, // Prevents flex item from overflowing
          }}
        >
          {renderSection(rightSection)}
        </div>
      </div>
    </div>
  );
}

// Helper function to render individual sections
function renderSection(section: SectionConfig) {
  switch (section.type) {
    case "travel":
      return (
        <TravelAnimationSection
          uniqueMapId={section.id}
          origin={section.data.origin}
          destination={section.data.destination}
          autoPlay={section.data.autoPlay}
          autoPlayDelayMs={section.data.autoPlayDelayMs}
          interactive={section.data.interactive || false}
          showRouteLine={section.data.showRouteLine || true}
          projection={section.data.projection || "mercator"}
        />
      );
    case "gallery":
      return (
        <GallerySection
          folderId={section.data.folderId}
          title={section.title}
          pageSize={section.data.pageSize}
          enableInfiniteLoading={section.data.enableInfiniteLoading}
          isSelectionEnabled={section.data.isSelectionEnabled}
          hiddenOptions={section.data.hiddenOptions}
          isMiniGallery={section.data.isMiniGallery}
          hardcodeSettings={section.data.hardcodeSettings}
        />
      );
    default:
      return (
        <div className="flex flex-col w-full h-full gap-4">
          <Text fz="md" c="dimmed">
            Unknown Section Type
          </Text>
          <div className="w-full h-full rounded-lg border border-grey-800 overflow-hidden flex items-center justify-center">
            <Text c="dimmed">Unknown section type: {section.type}</Text>
          </div>
        </div>
      );
  }
}
